import { displayCode, evaluateScan, NOT_RECEIVED_TAG, type ScanOutcome } from './cart';
import type { CartLine, CatalogItem } from './types';

/**
 * Laufzettel an der Kasse (Plan Welle 3, N-A): Die Codes eines Laufzettels werden NICHT mit eigenen Regeln geprüft. Jeder
 * Code läuft durch dieselbe Funktion wie ein Einzelscan (`evaluateScan`): unbekannt, schon verkauft, gelöscht, nicht
 * angenommen, doppelt, falsche Prüfziffer. Es gibt keine zweite Regel.
 */

export type RunSheetWarning = Extract<ScanOutcome, { kind: 'warning' }>;

export interface RunSheetHint {
  code: string;
  /** Kurzer Name für die Liste: Bezeichnung des Artikels, sonst der Code. */
  label: string;
  text: string;
}

export interface RunSheetImport {
  /** Codes laut Laufzettel. */
  total: number;
  /** Artikel, die ohne Rückfrage in den Warenkorb kommen (auch „nicht angenommen“, mit Hinweis). */
  lines: CartLine[];
  /** Rückfragen wie bei einem Einzelscan (schon verkauft, gelöscht, unbekannt): Die Kasse stellt sie nacheinander. */
  warnings: RunSheetWarning[];
  /** Alles, was nicht glatt lief (auch die Rückfragen), für die Zeile „N mit Hinweis“. */
  hints: RunSheetHint[];
}

/**
 * Übernimmt die Codes eines Laufzettels. `itemFor` liefert den Artikel im lokalen Stand der Kasse. `parkedNo` nennt die Nummer
 * eines geparkten Korbs, in dem der Artikel schon liegt: Solche Zeilen kommen nicht in den Warenkorb (sonst würde derselbe
 * Artikel zweimal kassiert), sie stehen bei den Hinweisen und lassen sich einzeln scannen (dort fragt die Kasse nach).
 * Die Reihenfolge des Laufzettels bleibt.
 */
export function importRunSheet(
  codes: readonly string[],
  labelSalt: number,
  itemFor: (code: string) => CatalogItem | undefined,
  cart: readonly CartLine[],
  parkedNo: (line: CartLine) => number | undefined = () => undefined,
): RunSheetImport {
  const lines: CartLine[] = [];
  const warnings: RunSheetWarning[] = [];
  const hints: RunSheetHint[] = [];
  let working: readonly CartLine[] = cart;
  for (const code of codes) {
    const item = itemFor(code);
    const outcome = evaluateScan(code, labelSalt, item, working);
    const label = item?.title ?? displayCode(code);
    if (outcome.kind === 'added') {
      const parked = parkedNo(outcome.line);
      if (parked !== undefined) {
        hints.push({
          code,
          label,
          text: `Liegt in geparktem Korb P${String(parked)}: bitte einzeln scannen.`,
        });
        continue;
      }
      lines.push(outcome.line);
      working = [...working, outcome.line];
      if (outcome.notReceived === true) hints.push({ code, label, text: NOT_RECEIVED_TAG });
    } else if (outcome.kind === 'warning') {
      warnings.push(outcome);
      hints.push({ code, label, text: outcome.message });
    } else {
      hints.push({ code, label, text: outcome.message });
    }
  }
  return { total: codes.length, lines, warnings, hints };
}
